import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Typography, Select, Popconfirm, Tag, Tooltip, message, Alert, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { taxTemplateService } from '../../../services/api';
import TaxTemplateModal from './modals/TaxTemplateModal';

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const TaxTemplatesTab = ({ 
  companies, 
  selectedCompany, 
  onCompanySelect 
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const screens = useBreakpoint();

  useEffect(() => {
    if (selectedCompany) {
      fetchTemplates(selectedCompany);
    } else {
        setTemplates([]);
    }
  }, [selectedCompany]);

  const fetchTemplates = async (companyId) => {
    try {
      setLoading(true);
      const response = await taxTemplateService.getTemplates(companyId);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Error al cargar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values) => {
    try {
      setModalLoading(true);
      await taxTemplateService.createTemplate(selectedCompany, values);
      message.success('Template creado exitosamente');
      setModalVisible(false);
      fetchTemplates(selectedCompany);
    } catch (error) {
      console.error('Error creating template:', error);
      message.error('Error al crear template');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdate = async (values) => {
    try {
      setModalLoading(true);
      await taxTemplateService.updateTemplate(selectedCompany, editingTemplate.id, values);
      message.success('Template actualizado exitosamente');
      setModalVisible(false);
      setEditingTemplate(null);
      fetchTemplates(selectedCompany);
    } catch (error) {
      console.error('Error updating template:', error);
      message.error('Error al actualizar template');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await taxTemplateService.deleteTemplate(selectedCompany, id);
      message.success('Template eliminado exitosamente');
      fetchTemplates(selectedCompany);
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Error al eliminar template');
      setLoading(false);
    }
  };

  const openModal = (template = null) => {
    setEditingTemplate(template);
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
            {text}
            {record.is_default && <Tag color="blue">Default</Tag>}
        </Space>
      )
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Impuestos',
      key: 'taxes',
      render: (_, record) => (
        <Space wrap>
          {record.details && record.details.map(detail => (
            <Tag key={detail.id}>
              {detail.company_tax?.tax_name} ({(parseFloat(detail.company_tax?.tasa_o_cuota || 0) * 100).toFixed(2)}%)
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="¿Estás seguro de eliminar este template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
          Plantillas de Impuestos
        </Title>
        
        <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ marginBottom: '16px', width: screens.xs ? '100%' : 'auto' }}>
          <Select
            placeholder="Selecciona una empresa"
            style={{ width: screens.xs ? '100%' : 400 }}
            value={selectedCompany}
            onChange={onCompanySelect}
          >
            {companies.map(company => (
              <Option key={company.id} value={company.id}>
                {company.tax_name}-{company.tax_id}
              </Option>
            ))}
          </Select>
          
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
            disabled={!selectedCompany}
            block={screens.xs}
          >
            Nueva Plantilla
          </Button>
        </Space>
      </div>

      <Alert
        message="Información"
        description="Gestiona grupos de impuestos para aplicar rápidamente en tus facturas."
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      {!selectedCompany ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          Selecciona una empresa para gestionar sus plantillas
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      )}

      <TaxTemplateModal
        visible={modalVisible}
        onCancel={() => {
            setModalVisible(false);
            setEditingTemplate(null);
        }}
        onOk={editingTemplate ? handleUpdate : handleCreate}
        loading={modalLoading}
        companyId={selectedCompany}
        initialValues={editingTemplate}
      />
    </Card>
  );
};

export default TaxTemplatesTab;
